import { useCallback, useEffect, useRef, useState } from 'react';

import type Fuse from 'fuse.js';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ExternalUser } from '@atlaskit/user-picker';

import type { SlackService } from '../index';

import type { Integration, IntegrationsResponse, SlackWorkspace } from './types';
import {
	createThirdPartyResolver,
	fuseFactory,
	getExistingIntegrations,
	initSlack,
	PLATFORM_SLACK_SERVICE,
	slackToExternalUser,
} from './utils';

export type ThirdPartyResolver = (query: string) => Promise<ExternalUser[]>;

export interface UseThirdPartyIntegrationsProps {
	cloudId: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	canUserInvite: () => boolean;
	onError?: (error: Error) => void;
}

export interface UseThirdPartyIntegrationsResult {
	clearIntegrations: () => void;
	initThirdPartyIntegrations: () => Promise<void>;
	isInitialized: boolean;
	isInitializing: boolean;
	thirdPartyResolver: ThirdPartyResolver;
}

export const useThirdPartyIntegrations = ({
	cloudId,
	createAnalyticsEvent,
	canUserInvite,
	onError,
}: UseThirdPartyIntegrationsProps): UseThirdPartyIntegrationsResult => {
	const slackServiceRef = useRef<SlackService | undefined>(undefined);
	const selectedSlackWorkspaceRef = useRef<SlackWorkspace | null>(null);
	const slackUserSearcherRef = useRef<Fuse<ExternalUser>>(fuseFactory([]));
	const [isInitializing, setIsInitializing] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [thirdPartyResolver, setThirdPartyResolver] = useState<ThirdPartyResolver>(() =>
		createThirdPartyResolver(cloudId, new Set<Integration>(), slackUserSearcherRef),
	);

	const setupSlackUserStreaming = useCallback(() => {
		slackUserSearcherRef.current = fuseFactory([]);

		if (selectedSlackWorkspaceRef.current && slackServiceRef.current) {
			slackServiceRef.current.streamUsers(selectedSlackWorkspaceRef.current.id, (slackUsers) => {
				if (slackUsers?.ok) {
					slackUsers.result.forEach((slackUser) => {
						const externalUser = slackToExternalUser(slackUser);
						slackUserSearcherRef.current.add(externalUser);
					});
				}
			});
		}
	}, []);

	const clearIntegrations = useCallback(() => {
		if (slackServiceRef.current) {
			slackServiceRef.current.abort();
		}

		slackServiceRef.current = undefined;
		selectedSlackWorkspaceRef.current = null;
		slackUserSearcherRef.current = fuseFactory([]);

		setThirdPartyResolver(() =>
			createThirdPartyResolver(cloudId, new Set<Integration>(), slackUserSearcherRef),
		);
	}, [cloudId]);

	const initSlackIntegration = useCallback(
		async (existingIntegrations: IntegrationsResponse) => {
			setIsInitialized(false);
			setIsInitializing(true);

			try {
				if (slackServiceRef.current) {
					slackServiceRef.current.abort();
				}

				const result = await initSlack(cloudId, createAnalyticsEvent, existingIntegrations);
				slackServiceRef.current = result.slackService;

				if (result.selectedWorkspace) {
					selectedSlackWorkspaceRef.current = result.selectedWorkspace;
				}

				setupSlackUserStreaming();

				setIsInitialized(true);
			} catch (error) {
				onError?.(
					error instanceof Error ? error : new Error('Failed to initialize Slack integration'),
				);
			} finally {
				setIsInitializing(false);
			}
		},
		[cloudId, createAnalyticsEvent, onError, setupSlackUserStreaming],
	);

	const initThirdPartyIntegrations = useCallback(async () => {
		if (!canUserInvite()) {
			return;
		}

		const existingIntegrations = await getExistingIntegrations(cloudId);
		const connectedIntegrations = new Set<Integration>(existingIntegrations.connectedIntegrations);

		if (!connectedIntegrations) {
			return;
		}

		if (connectedIntegrations.has(PLATFORM_SLACK_SERVICE) && !slackServiceRef.current) {
			initSlackIntegration(existingIntegrations);
		}

		setThirdPartyResolver(() =>
			createThirdPartyResolver(cloudId, connectedIntegrations, slackUserSearcherRef),
		);

		setIsInitialized(true);
	}, [cloudId, initSlackIntegration, canUserInvite]);

	useEffect(() => {
		return () => {
			if (slackServiceRef.current) {
				slackServiceRef.current.abort();
			}
		};
	}, []);

	return {
		isInitializing,
		isInitialized,
		initThirdPartyIntegrations,
		clearIntegrations,
		thirdPartyResolver,
	};
};
